import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
  durationStringToMinutes,
  TeamEventMeetingType,
} from '@vendasta/meetings';
import { ServiceEventType } from '@vendasta/meetings';
import { of } from 'rxjs';
import { catchError, skip } from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { MeetingType } from '../interface';
import { CartItem } from '../shared/booking-cart/booking-cart.component';
import { RemoveServiceDialogComponent } from '../scheduling-page/remove-service-dialog/remove-service-dialog.component';
import { meetingTypeToServiceEventType } from '../util';

interface ProviderInfo {
  id: string;
  name: string;
  subtitle: string;
  imageUrl: string;
  initial: string;
}

interface ServiceGroup {
  id: string;
  name: string;
  description: string;
  durationMinutes: number;
  meetingType: TeamEventMeetingType;
  providers: ProviderInfo[];
  selectedProviderId: string;
  expanded: boolean;
}

type ProviderSelectionMode = 'all' | 'per-service';

@Component({
  selector: 'app-provider-selection-page',
  standalone: false,
  templateUrl: './provider-selection-page.component.html',
  styleUrls: ['./provider-selection-page.component.scss'],
})
export class ProviderSelectionPageComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly storeService = inject(BookMeNowStoreService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('filterWrapper') filterWrapper?: ElementRef<HTMLElement>;

  readonly TeamEventMeetingType = TeamEventMeetingType;

  loading = signal(true);
  isSingleEventRoute = signal(false);
  providerSelectionMode = signal<ProviderSelectionMode>('all');
  dropdownOpen = signal<boolean>(false);
  serviceGroups = signal<ServiceGroup[]>([]);

  clientChoiceGroups = computed(() =>
    this.serviceGroups().filter(
      (g) => g.meetingType === TeamEventMeetingType.CLIENT_CHOICE && g.providers.length > 1
    )
  );

  canUseAllMode = computed(() => {
    return this.clientChoiceGroups().length > 1;
  });

  totalDurationLabel = computed(() => {
    const totalMin = this.serviceGroups().reduce(
      (sum, g) => sum + g.durationMinutes,
      0
    );
    const hrs = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    if (hrs === 0) return `${mins}min`;
    if (mins === 0) return `${hrs}hr`;
    return `${hrs}hr ${mins}min`;
  });

  commonProviders = computed(() => {
    const groups = this.clientChoiceGroups();
    if (groups.length === 0) return [];
    const commonIds = new Set(groups[0].providers.map((p) => p.id));
    for (let i = 1; i < groups.length; i++) {
      const ids = new Set(groups[i].providers.map((p) => p.id));
      for (const id of commonIds) {
        if (!ids.has(id)) commonIds.delete(id);
      }
    }
    return groups[0].providers.filter((p) => commonIds.has(p.id));
  });

  allSelectedProviderId = computed(() => {
    const groups = this.serviceGroups();
    if (groups.length === 0) return 'any';
    const firstId = groups[0].selectedProviderId;
    return groups.every((g) => g.selectedProviderId === firstId)
      ? firstId
      : 'any';
  });

  cartItems = computed<CartItem[]>(() =>
    this.serviceGroups().map((g) => {
      let providerName = 'Any available';
      if (g.meetingType === TeamEventMeetingType.MULTI_HOST) {
        providerName =
          g.providers.map((p) => p.name).join(', ') || 'Any available';
      } else if (g.providers.length === 1) {
        providerName = g.providers[0].name;
      } else if (g.selectedProviderId !== 'any') {
        providerName =
          g.providers.find((p) => p.id === g.selectedProviderId)?.name ||
          'Any available';
      }
      return {
        name: g.name,
        providerName,
        duration: g.durationMinutes,
        initials: g.name
          .split(' ')
          .map((w) => w[0])
          .join('')
          .substring(0, 2)
          .toUpperCase(),
      };
    })
  );

  modeLabel = computed(() =>
    this.providerSelectionMode() === 'all'
      ? 'Show provider for all services'
      : 'Choose provider per service'
  );

  ctaDisabled = computed(() => this.serviceGroups().length === 0);

  ngOnInit(): void {
    const params = this.route.snapshot.params;
    this.isSingleEventRoute.set(
      !!params['calendarId'] && !!params['meetingTypeId']
    );
    this.providerSelectionMode.set(
      this.storeService.getProviderSelectionMode()
    );
    if (params['calendarId'] && params['meetingTypeId']) {
      // Check if events already in store (e.g., navigating back from datetime)
      const existingEvents = this.storeService.getSelectedBookingEvents();
      if (existingEvents.length > 0) {
        const groups = existingEvents.map((e) => this.mapEventToServiceGroup(e));
        this.serviceGroups.set(groups);

        if (!this.storeService.getShouldShowProviderStep()) {
          this.storeService.setBookingStep('datetime');
          return;
        }

        this.loading.set(false);
      } else {
        this.initFromBookFlow(params['calendarId'], params['meetingTypeId']);
      }
    } else if (
      params['serviceId'] ||
      params['groupId'] ||
      params['calendarId']
    ) {
      this.initFromServiceFlow();
    } else {
      this.loading.set(false);
    }
  }

  toggleDropdown(): void {
    this.dropdownOpen.update((v) => !v);
  }

  setMode(mode: ProviderSelectionMode): void {
    if (this.providerSelectionMode() === mode) {
      this.dropdownOpen.set(false);
      return;
    }
    this.providerSelectionMode.set(mode);
    this.storeService.setProviderSelectionMode(mode);
    this.dropdownOpen.set(false);
    this.serviceGroups.update((groups) =>
      groups.map((g) => ({ ...g, selectedProviderId: 'any' }))
    );
    this.storeService.setSelectedHost(null);
  }

  toggleServiceExpanded(index: number): void {
    this.serviceGroups.update((groups) =>
      groups.map((g, i) => (i === index ? { ...g, expanded: !g.expanded } : g))
    );
  }

  selectProviderForService(serviceId: string, providerId: string): void {
    this.serviceGroups.update((groups) =>
      groups.map((g) =>
        g.id === serviceId ? { ...g, selectedProviderId: providerId } : g
      )
    );
    this.storeService.setSelectedHost(providerId === 'any' ? null : providerId);
  }

  selectProviderForAll(providerId: string): void {
    this.serviceGroups.update((groups) =>
      groups.map((g) => ({ ...g, selectedProviderId: providerId }))
    );
    this.storeService.setSelectedHost(providerId === 'any' ? null : providerId);
  }

  onRemoveCartItem(index: number): void {
    const removed = this.serviceGroups()[index];
    if (!removed) {
      return;
    }

    const dialogRef = this.dialog.open(RemoveServiceDialogComponent, {
      data: { serviceName: removed.name },
      width: '540px',
      maxWidth: '90vw',
      panelClass: 'remove-service-dialog-panel',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.serviceGroups.update((groups) =>
            groups.filter((_, i) => i !== index)
          );

          const currentEvents = this.storeService.getSelectedBookingEvents();
          this.storeService.setSelectedBookingEvents(
            currentEvents.filter((e) => e.id !== removed.id)
          );

          if (removed.selectedProviderId !== 'any') {
            const currentHost = this.storeService.getSelectedHost();
            if (currentHost === removed.selectedProviderId) {
              this.storeService.setSelectedHost(null);
            }
          }
        }
      });
  }

  onChooseDateAndTime(): void {
    const { providerMap, hostMap } = this.buildNonClientChoiceProviderMaps();
    for (const group of this.serviceGroups()) {
      if (!this.needsProviderSelection(group.meetingType, group.providers)) {
        continue;
      }
      const cartItem = this.cartItems().find((c) => c.name === group.name);
      if (cartItem) {
        providerMap.set(group.id, cartItem.providerName);
      }
      if (group.selectedProviderId !== 'any') {
        hostMap.set(group.id, group.selectedProviderId);
      }
    }
    this.storeService.setEventProviderMap(providerMap);
    this.storeService.setEventHostMap(hostMap);
    this.storeService.setBookingStep('datetime');
  }

  onBack(): void {
    if (this.isSingleEventRoute()) {
      return;
    }
    this.storeService.setBookingStep('scheduling');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.dropdownOpen() &&
      !this.filterWrapper?.nativeElement.contains(event.target as Node)
    ) {
      this.dropdownOpen.set(false);
    }
  }

  private initFromServiceFlow(): void {
    const events = this.storeService.getSelectedBookingEvents();
    if (events.length > 0) {
      const groups = events.map((e) => this.mapEventToServiceGroup(e));
      this.serviceGroups.set(groups);

      if (!this.storeService.getShouldShowProviderStep()) {
        this.autoAssignNonClientChoiceProviders();
        this.storeService.setBookingStep('datetime');
        return;
      }

      this.autoAssignNonClientChoiceProviders();
      this.loading.set(false);
      this.subscribeToEventUpdates();
    } else {
      this.storeService.setBookingStep('scheduling');
    }
  }

  private subscribeToEventUpdates(): void {
    this.storeService.selectedBookingEvents$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((events) => {
        this.serviceGroups.set(
          events.map((e) => this.mapEventToServiceGroup(e))
        );
      });
  }

  private initFromBookFlow(calendarId: string, meetingTypeId: string): void {
    this.storeService
      .loadMeetingTypeForGivenCalendarIdAndSlug(calendarId, meetingTypeId)
      .pipe(
        catchError((err) => {
          this.storeService.handleMeetingTypeError(err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((mt) => {
        if (mt) {
          const meetingType = mt as MeetingType;
          const serviceGroup = this.mapMeetingTypeToServiceGroup(meetingType);
          this.serviceGroups.set([serviceGroup]);

          const eventData = meetingTypeToServiceEventType(meetingType);
          this.storeService.setSelectedBookingEvents([eventData]);
          this.storeService.setBookingOriginId(meetingType.id || '');

          if (!this.needsProviderSelection(meetingType.meetingType, serviceGroup.providers)) {
            this.storeService.setShouldShowProviderStep(false);
            if (serviceGroup.providers.length === 1) {
              const providerMap = new Map<string, string>();
              const hostMap = new Map<string, string>();
              providerMap.set(serviceGroup.id, serviceGroup.providers[0].name);
              hostMap.set(serviceGroup.id, serviceGroup.providers[0].id);
              this.storeService.setEventProviderMap(providerMap);
              this.storeService.setEventHostMap(hostMap);
              this.storeService.setSelectedHost(serviceGroup.providers[0].id);
            }
            this.storeService.setBookingStep('datetime');
            return;
          }
          this.storeService.setShouldShowProviderStep(true);
        }
        this.loading.set(false);
      });
  }

  private buildNonClientChoiceProviderMaps(): {
    providerMap: Map<string, string>;
    hostMap: Map<string, string>;
  } {
    const providerMap = new Map<string, string>();
    const hostMap = new Map<string, string>();
    for (const group of this.serviceGroups()) {
      if (this.needsProviderSelection(group.meetingType, group.providers)) {
        continue;
      }
      if (group.meetingType === TeamEventMeetingType.MULTI_HOST) {
        providerMap.set(group.id, group.providers.map((p) => p.name).join(', ') || 'Any available');
      } else if (group.providers.length === 1) {
        providerMap.set(group.id, group.providers[0].name);
        hostMap.set(group.id, group.providers[0].id);
      } else if (group.providers.length > 1) {
        providerMap.set(group.id, group.providers.map((p) => p.name).join(', '));
      }
    }
    return { providerMap, hostMap };
  }

  private autoAssignNonClientChoiceProviders(): void {
    const { providerMap, hostMap } = this.buildNonClientChoiceProviderMaps();
    this.storeService.setEventProviderMap(providerMap);
    this.storeService.setEventHostMap(hostMap);
  }

  private needsProviderSelection(
    meetingType: TeamEventMeetingType | undefined,
    providers: ProviderInfo[]
  ): boolean {
    const type = meetingType ?? TeamEventMeetingType.ROUND_ROBIN;
    if (type !== TeamEventMeetingType.CLIENT_CHOICE) {
      return false;
    }
    return providers.length > 1;
  }

  private mapEventToServiceGroup(event: ServiceEventType): ServiceGroup {
    const meetingType = event.eventType ?? TeamEventMeetingType.ROUND_ROBIN;
    const providers = this.buildProvidersFromHostUsers(
      event.hostUser || [],
      meetingType
    );
    const eventHostMap = this.storeService.getEventHostMap();
    const existingHost = eventHostMap.get(event.id);
    let selectedProviderId: string;
    if (existingHost && providers.some((p) => p.id === existingHost)) {
      selectedProviderId = existingHost;
    } else if (providers.length === 1) {
      selectedProviderId = providers[0].id;
    } else {
      selectedProviderId = 'any';
    }
    return {
      id: event.id,
      name: event.name || 'Service',
      description: event.description || '',
      durationMinutes: event.duration
        ? durationStringToMinutes({ duration: event.duration, round: true }) ||
          0
        : 0,
      meetingType,
      providers,
      selectedProviderId,
      expanded: true,
    };
  }

  private mapMeetingTypeToServiceGroup(mt: MeetingType): ServiceGroup {
    const meetingType = mt.meetingType ?? TeamEventMeetingType.ROUND_ROBIN;
    const providers = this.buildProvidersFromHostUsers(
      mt.hostUsers || [],
      meetingType,
      mt.hostOrderDetails
    );
    const eventHostMap = this.storeService.getEventHostMap();
    const existingHost = eventHostMap.get(mt.id);
    let selectedProviderId: string;
    if (existingHost && providers.some((p) => p.id === existingHost)) {
      selectedProviderId = existingHost;
    } else if (providers.length === 1) {
      selectedProviderId = providers[0].id;
    } else {
      selectedProviderId = 'any';
    }
    return {
      id: mt.id,
      name: mt.name || 'Service',
      description: mt.description || '',
      durationMinutes:
        durationStringToMinutes({ duration: mt.duration, round: true }) || 0,
      meetingType,
      providers,
      selectedProviderId,
      expanded: true,
    };
  }

  private buildProvidersFromHostUsers(
    hostUsers: {
      userId?: string;
      displayName?: string;
      isConnected?: boolean;
    }[],
    meetingType: TeamEventMeetingType,
    orderDetails?: { userId?: string; hostOrder?: number }[]
  ): ProviderInfo[] {
    if (!hostUsers?.length) return [];

    const hasConnected = hostUsers.some((h) => h.isConnected);
    const filtered = hasConnected
      ? hostUsers.filter((h) => h.isConnected)
      : hostUsers;

    const providers: ProviderInfo[] = filtered.map((h) => ({
      id: h.userId || '',
      name: h.displayName || 'Unknown',
      subtitle: '',
      imageUrl: '',
      initial: (h.displayName || 'U').charAt(0).toUpperCase(),
    }));

    if (
      meetingType === TeamEventMeetingType.PRIORITY_ROUND_ROBIN &&
      orderDetails?.length
    ) {
      const orderMap = new Map(
        orderDetails.map((d) => [d.userId, d.hostOrder ?? 0])
      );
      providers.sort(
        (a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
      );
    }

    return providers;
  }
}
